import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { geoMercator, GeoPath, geoPath } from 'd3-geo'
import { PlaceLabel } from 'components/PlaceLabel'
import { RegionLabel } from 'components/RegionLabel'
import { useElementSize } from 'usehooks-ts'
import { AnalysisSourceTemplate } from 'aws-sdk/clients/quicksight'
import { AnalysisStatus } from 'aws-sdk/clients/iotevents'

const Zoom = require('../GeoJSON/zoom.json')
const Zoom_Desktop = require('../GeoJSON/zoom_desktop.json')
const BaseMap = require('../GeoJSON/country.json')
const RegionsMap = require('../GeoJSON/regions.json')
const Sea = require('../GeoJSON/water.json')
const Cities = require('../GeoJSON/cities.json')

export const UkraineMap = ({
  data,
  height,
  russianControl,
  russianAdvance,
  uaCounter,
  regionLabels,
  mapLabels,
  colorMode,
  index,
  date,
}: {
  data?: any
  height: number
  russianControl?: any
  russianAdvance?: any
  uaCounter?: any
  regionLabels?: RegionLabelType[]
  mapLabels?: MapLabelType[]
  colorMode: 'light' | 'dark'
  index: number
  date?: string
}) => {
  const [container, size] = useElementSize()
  const [isMobile, setMobile] = useState(false)
  const [width, setSize] = useState<number>(500)

  let projection = geoMercator().fitSize(
    [width, height],
    isMobile ? (Zoom as any) : (Zoom_Desktop as any)
  )
  let geoPathGenerator: GeoPath<any, any> = geoPath().projection(projection)

  useEffect(() => {
    if (size.width === width) return

    setSize(size.width)
    setMobile(size.width < 712)
    projection = geoMercator().fitSize(
      [size.width, size.height * 0.7],
      RegionsMap as any
    )
    geoPathGenerator = geoPath().projection(projection)
  }, [size.width])

  return (
    <MapContainer ref={container} className='MapContainer'>
      <StyledSVG width={width} height={height} colorMode={colorMode}>
        <defs>
          <pattern
            id='diagonal-stripe-1'
            patternUnits='userSpaceOnUse'
            width='4'
            height='4'
          >
            <path
              d='M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2'
              style={{
                stroke:
                  colorMode === 'light' ? 'var(--red-main)' : 'var(--red-main)',
                strokeWidth: 1.25,
              }}
            />
          </pattern>
          <pattern
            id='diagonal-stripe-2'
            patternUnits='userSpaceOnUse'
            width='4'
            height='4'
          >
            <path
              d='M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2'
              style={{
                stroke:
                  colorMode === 'light'
                    ? 'var(--brandblue-main)'
                    : 'var(--blue-main)',
                strokeWidth: 1,
              }}
            />
          </pattern>
        </defs>
        <g>
          <g className='country'>
            <CountryShape colorMode={colorMode} d={geoPathGenerator(BaseMap)} />
          </g>
          <g className='regions'>
            <Regions d={geoPathGenerator(RegionsMap)} colorMode={colorMode} />
          </g>

          <g className='occupied'>
            {russianControl && (
              <Occupied
                d={geoPathGenerator(russianControl)}
                colorMode={colorMode}
              />
            )}
          </g>
          <g className='regions'>
            <CityRegions d={geoPathGenerator(Cities)} colorMode={colorMode} />
          </g>
          <g className='advance'>
            {russianAdvance && (
              <Advances
                d={geoPathGenerator(russianAdvance)}
                colorMode={colorMode}
              />
            )}
          </g>
          <g className='advance'>
            {uaCounter && (
              <Counter d={geoPathGenerator(uaCounter)} colorMode={colorMode} />
            )}
          </g>

          <g className='regions'>
            <Seas d={geoPathGenerator(Sea)} colorMode={colorMode} />
          </g>
          <g className='country'>
            <CountrySecondOutline
              colorMode={colorMode}
              d={geoPathGenerator(BaseMap)}
            />
            <CountryOutline
              colorMode={colorMode}
              d={geoPathGenerator(BaseMap)}
            />
          </g>

          <g className='bombs'>
            {data &&
              data.features
                // .filter((f: any) =>
                //   !date
                //     ? true
                //     : new Date(f.properties.acq_date) <= new Date(date)
                // )
                .map(({ geometry, properties }, i: number) => {
                  let pos = projection(geometry.coordinates)
                  return (
                    <React.Fragment key={'Bomb_' + i}>
                      {
                        <Bomb
                          pos={pos}
                          r={isMobile ? 1 : 1.5}
                          isAir={true}
                          colorMode={colorMode}
                        />
                      }
                    </React.Fragment>
                  )
                })}
          </g>
          <g className='region_labels'>
            {regionLabels &&
              regionLabels.map((m, i) => {
                let pos = projection(m.pos)
                return (
                  <RegionLabel
                    key={'Region_' + i}
                    x={pos[0]}
                    y={pos[1]}
                    data={{
                      pos: m.pos,
                      r: [(width / 100) * m.r[0], (width / 100) * m.r[1]],
                    }}
                  />
                )
              })}
          </g>
          <g className='map_labels'>
            {mapLabels &&
              mapLabels.map((m, i) => {
                let pos = projection(m.pos)
                return (
                  <PlaceLabel
                    key={'Place_' + i}
                    x={pos[0]}
                    y={pos[1]}
                    data={m}
                  />
                )
              })}
          </g>
        </g>
        {data && (
          <DateGroup className='date' colorMode={colorMode}>
            <rect x={0} y={10} height={50} width={350} />
            <circle cx={20} cy={25} r={5} />
            <text x={40} y={30}>
              Von Satelliten gemessene Hitzeanomalien
            </text>
            <text x={40} y={50} fontWeight={700}>
              28. August bis 8. September
              {/* {new Date(date).toLocaleDateString('de-CH', {
                day: 'numeric',
                month: 'long',
              })} */}
            </text>
          </DateGroup>
        )}
      </StyledSVG>
    </MapContainer>
  )
}

const Bomb = ({
  pos,
  r,
  isAir,
  colorMode,
}: {
  pos: [number, number]
  r: number
  isAir: boolean
  colorMode: 'light' | 'dark'
}) => {
  return (
    <BombMarker
      cx={pos[0]}
      cy={pos[1]}
      r={r}
      opacity={1}
      current={isAir}
      colorMode={colorMode}
      fill={colorMode === 'light' ? '#202346' : '#E16D00'}
    />
  )
}
const MapContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: inline-block;
  flex: 0 0 70%;
  margin-top: 5%;
  margin-bottom: 15px;
  overflow: hidden;
  @media only screen and (max-width: 711px) {
    flex: 0 0 70%;
  }
`
const StyledSVG = styled.svg<{ colorMode: 'light' | 'dark' }>`
  overflow: visible;
`

const DateGroup = styled.g<{ colorMode: 'light' | 'dark' }>`
  font-family: var(--font-sans-serif);
  circle {
    fill: ${({ colorMode }) =>
      colorMode === 'light' ? 'var(--red-main)' : 'var(--orange-main)'};
  }
  rect {
    fill: var(--site-background);
  }
  text {
    fill: var(--text-color);
    font-size: 14px;
  }
`
const CountryShape = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) =>
    colorMode === 'light' ? 'var(--site-background)' : 'var(--gray-2)'};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 1 : 0.4)};
`
const CountryOutline = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: none;
  stroke-linejoin: round;
  stroke: #7d7d7d;
  stroke: var(--text-color);
  opacity: 0.7;
`
const CountrySecondOutline = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: none;

  stroke-linejoin: round;
  opacity: 0.7;
  stroke: var(--site-background);
  stroke-width: 5;
  opacity: 0.7;
`
const Regions = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: none;
  opacity: 1;
  stroke: ${({ colorMode }) => (colorMode === 'light' ? '#d4d4d4' : '#3e3e3e')};
  stroke-width: 1;
`
const Seas = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) =>
    colorMode === 'light'
      ? ' var(--blue-bright-3)'
      : ' var(--site-background)'};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 1 : 1)};
  stroke: ${({ colorMode }) => (colorMode === 'light' ? '#d4d4d4' : '#3e3e3e')};
  stroke-width: 1;
  stroke: ${({ colorMode }) =>
    colorMode === 'light'
      ? 'var(--site-background)'
      : 'var(--site-background)'};
  stroke-width: 1;
`
const CityRegions = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) =>
    colorMode === 'light' ? 'var(--brandblue-main)' : 'var(--beige-bright-2)'};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.3 : 0.4)};
  stroke: none;
`

const Occupied = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) =>
    colorMode === 'light' ? 'var(--red-bright-3)' : '#D3343F'};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 1 : 0.25)};
`

const Advances = styled.path<{ colorMode: 'light' | 'dark' }>`
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.4 : 0.8)};
  fill: url(#diagonal-stripe-1);
  stroke: var(--site-background);
  stroke-width: 1.5;
`
const Counter = styled.path<{ colorMode: 'light' | 'dark' }>`
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.4 : 0.8)};
  fill: url(#diagonal-stripe-2);
  stroke: var(--site-background);
  stroke-width: 1.5;
`
const BombMarker = styled.circle<{
  current: boolean
  colorMode: 'light' | 'dark'
}>`
  fill: ${({ current, colorMode }) =>
    current
      ? colorMode === 'light'
        ? 'var(--red-main)'
        : '#E16D00'
      : colorMode === 'light'
      ? 'var(--gray-4)'
      : '#E78D38'};
`
